<template>
  <div
    class="min-h-screen w-full"
    :style="{ backgroundColor: pageBackgroundColor }"
  >
    <div
      class="flex flex-col items-center w-full bg-contrast-background min-h-screen overflow-hidden min-[1001px]:hidden"
    >
      <div class="flex min-h-screen w-full flex-col px-5 pb-48 pt-5">
        <div class="mb-5 flex gap-2">
          <button
            v-for="(step, index) in reservationSteps"
            :key="step.key"
            class="h-1.5 flex-1 rounded-full transition"
            :class="index <= currentStepIndex ? '' : 'bg-gray-200'"
            :style="
              index <= currentStepIndex
                ? { backgroundColor: buttonColor }
                : null
            "
            :disabled="index > currentStepIndex"
            :aria-label="step.label"
            type="button"
            @click="goToStep(step.key)"
          />
        </div>

        <div class="flex-1">
          <div
            class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
          >
            {{ currentStepLabel }}
          </div>

          <div v-if="reservationStep === 'details'">
            <div class="space-y-3">
              <button
                v-for="location in locationsForSelector"
                :key="location.id"
                class="flex w-full items-center justify-between rounded-small border bg-white px-4 py-4 text-left transition"
                :class="
                  location.id === activeLocation.id
                    ? ''
                    : 'border-gray-200 text-blue-900'
                "
                :style="locationCardStyle(location.id === activeLocation.id)"
                type="button"
                @click="handleLocationSelected(location)"
              >
                <span class="min-w-0">
                  <span class="block truncate font-bold">
                    {{ location.name }}
                  </span>
                  <span class="mt-1 block truncate text-sm text-gray-400">
                    {{ location.address || location.city }}
                  </span>
                </span>
                <span
                  class="ml-3 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                  :class="{
                    'text-white': location.id === activeLocation.id,
                    'text-gray-300': location.id !== activeLocation.id,
                  }"
                  :style="
                    location.id === activeLocation.id
                      ? { backgroundColor: buttonColor }
                      : null
                  "
                >
                  <c-icon name="CheckRound" size="0.9rem" />
                </span>
              </button>
            </div>

            <div v-if="roomOptions.length > 1" class="mt-5">
              <div
                class="mb-2 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
              >
                {{ $t("reservations.area") }}
              </div>
              <div class="flex flex-row overflow-x-auto space-x-3 pb-1">
                <button
                  v-for="room in roomOptions"
                  :key="room.id || 'any'"
                  class="min-w-[90px] rounded-small border px-3 py-3 text-sm font-semibold"
                  :class="
                    selectedRoomId === room.id
                      ? ''
                      : 'bg-white text-blue-900 border-gray-200'
                  "
                  :style="selectedChoiceStyle(selectedRoomId === room.id)"
                  type="button"
                  @click="selectedRoomId = room.id"
                >
                  {{ room.name }}
                </button>
              </div>
            </div>

            <div class="mt-5">
              <div
                class="mb-2 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
              >
                {{ $t("reservations.party-size") }}
              </div>
              <div class="grid grid-cols-5 gap-2">
                <button
                  v-for="size in partySizes"
                  :key="size"
                  class="h-12 rounded-small border text-base font-bold"
                  :class="
                    partySize === size
                      ? ''
                      : 'bg-white text-blue-900 border-gray-200'
                  "
                  :style="selectedChoiceStyle(partySize === size)"
                  type="button"
                  @click="partySize = size"
                >
                  {{ size }}
                </button>
              </div>
            </div>
          </div>

          <div v-else-if="reservationStep === 'datetime'">
            <div
              :class="calendarPanelClass"
              class="flex justify-center scale-125 py-10"
            >
              <n-date-picker
                v-model:value="selectedDay"
                panel
                size="large"
                :is-date-disabled="dateIsDisabled"
                :first-day-of-week="0"
                :actions="[]"
              >
                <template #prev-year>
                  <div />
                </template>
                <template #prev-month>
                  <c-icon
                    name="ArrowBackIosRound"
                    size="0.9rem"
                    class="text-blue-900"
                  />
                </template>
                <template #next-month>
                  <c-icon
                    name="ArrowForwardIosRound"
                    size="0.9rem"
                    class="text-blue-900"
                  />
                </template>
                <template #next-year>
                  <div />
                </template>
              </n-date-picker>
            </div>

            <div v-if="loadingAvailability" class="flex justify-center py-10">
              <c-loading-spinner />
            </div>
            <div v-else-if="availabilityGroups.length" class="mt-5 space-y-4">
              <div v-for="group in availabilityGroups" :key="group.label">
                <div class="mb-2 text-sm font-semibold text-gray-400">
                  {{ group.label }}
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <button
                    v-for="slot in group.slots"
                    :key="`${group.label}-${slot.time}`"
                    class="h-12 rounded-small border font-semibold"
                    :class="slotClass(slot)"
                    :style="slotStyle(slot)"
                    :disabled="!slot.available"
                    type="button"
                    @click="selectedSlot = slot.time"
                  >
                    {{ slot.time }}
                  </button>
                </div>
              </div>
            </div>
            <div
              v-else
              class="rounded-small bg-white p-5 text-center font-semibold text-gray-400"
            >
              {{ $t("reservations.no-slots") }}
            </div>
          </div>

          <div v-else>
            <n-input
              v-model:value="comment"
              type="textarea"
              :autosize="{ minRows: 4, maxRows: 6 }"
              :placeholder="$t('reservations.comment-placeholder')"
            />
          </div>
        </div>

        <div
          class="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-contrast-background px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-8px_24px_rgba(34,33,52,0.08)]"
        >
          <div class="bg-white border border-gray-200 rounded-small p-4 mb-3">
            <div class="flex items-center gap-2 text-blue-900">
              <c-icon name="EventAvailableRound" size="1.2rem" />
              <div class="text-sm font-semibold">{{ summaryText }}</div>
            </div>
          </div>

          <div class="flex gap-3">
            <button
              v-if="!isFirstStep"
              class="w-[112px] rounded-small border border-gray-200 bg-white py-4 font-bold text-blue-900"
              type="button"
              @click="goPreviousStep"
            >
              {{ $t("reservations.back") }}
            </button>
            <button
              class="flex-1 rounded-small border py-4 text-lg font-bold"
              :class="
                canContinue ? '' : 'bg-gray-200 text-gray-400 border-gray-200'
              "
              :style="canContinue ? selectedChoiceStyle(true) : null"
              :disabled="!canContinue"
              type="button"
              @click="handlePrimaryStepAction"
            >
              {{ primaryActionText }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="hidden min-h-screen w-full items-center justify-center px-6 py-10 min-[1001px]:flex"
    >
      <div
        class="grid w-full max-w-[1040px] overflow-hidden rounded-big bg-[#FCF8F4] shadow-xl min-[1001px]:grid-cols-[380px_minmax(0,1fr)]"
      >
        <section
          class="relative min-h-[660px] overflow-hidden bg-blue-900 text-white"
        >
          <c-image
            v-if="heroImageId"
            :image-id="heroImageId"
            class="absolute inset-0 h-full w-full object-cover"
            mode="fill"
          />
          <div
            v-else
            class="absolute inset-0"
            :style="{ backgroundColor: buttonColor }"
          />
          <div
            class="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.44)_48%,rgba(0,0,0,0.74)_100%)]"
          />
          <div
            class="relative z-10 flex h-full min-h-[660px] flex-col justify-between p-9"
          >
            <div>
              <div
                class="inline-flex items-center rounded-full px-3 py-2 text-xs font-bold"
                :style="selectedChoiceStyle(true)"
              >
                {{ $t("reservations.signup-subtitle") }}
              </div>
              <h1 class="mt-8 text-[46px] leading-none font-bold">
                {{ heroTitle }}
              </h1>
              <div
                v-if="heroSubtitle"
                class="mt-4 max-w-[280px] text-xs font-bold uppercase tracking-[0.16em] text-white"
              >
                {{ heroSubtitle }}
              </div>
            </div>

            <div
              v-if="locationDetails.length"
              class="rounded-small border border-white/20 bg-[rgba(0,0,0,0.38)] px-5 py-3 text-sm backdrop-blur"
            >
              <div
                v-for="(detail, index) in locationDetails"
                :key="detail.icon"
                class="flex items-center gap-3 py-3"
                :class="{ 'border-t border-white/15': index > 0 }"
              >
                <c-icon :name="detail.icon" size="1.1rem" class="text-white" />
                <span>{{ detail.text }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="flex min-h-[660px] flex-col px-11 py-10 text-blue-900">
          <div class="mb-7 flex gap-2">
            <button
              v-for="(step, index) in reservationSteps"
              :key="step.key"
              class="h-1.5 flex-1 rounded-full transition"
              :class="index <= currentStepIndex ? '' : 'bg-gray-200'"
              :style="
                index <= currentStepIndex
                  ? { backgroundColor: buttonColor }
                  : null
              "
              :disabled="index > currentStepIndex"
              :aria-label="step.label"
              type="button"
              @click="goToStep(step.key)"
            />
          </div>

          <div class="flex-1">
            <div
              class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
            >
              {{ currentStepLabel }}
            </div>

            <div v-if="reservationStep === 'details'">
              <div
                class="grid grid-cols-[minmax(0,360px)_minmax(0,330px)] gap-8"
              >
                <div>
                  <div
                    class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
                  >
                    {{ $t("reservations.location") }}
                  </div>
                  <div class="max-h-[178px] space-y-2 overflow-y-auto pr-1">
                    <button
                      v-for="location in locationsForSelector"
                      :key="location.id"
                      class="flex w-full items-center justify-between rounded-small border bg-white px-4 py-3 text-left transition"
                      :class="
                        location.id === activeLocation.id
                          ? ''
                          : 'border-gray-200 text-blue-900'
                      "
                      :style="
                        locationCardStyle(location.id === activeLocation.id)
                      "
                      type="button"
                      @click="handleLocationSelected(location)"
                    >
                      <span class="min-w-0">
                        <span class="block truncate font-bold">
                          {{ location.name }}
                        </span>
                        <span class="mt-1 block truncate text-xs text-gray-400">
                          {{ location.address || location.city }}
                        </span>
                      </span>
                      <span
                        class="ml-3 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                        :class="{
                          'text-white': location.id === activeLocation.id,
                          'text-gray-300': location.id !== activeLocation.id,
                        }"
                        :style="
                          location.id === activeLocation.id
                            ? { backgroundColor: buttonColor }
                            : null
                        "
                      >
                        <c-icon name="CheckRound" size="0.9rem" />
                      </span>
                    </button>
                  </div>

                  <div v-if="roomOptions.length > 1" class="mt-5">
                    <div
                      class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
                    >
                      {{ $t("reservations.area") }}
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <button
                        v-for="room in roomOptions"
                        :key="room.id || 'any'"
                        class="rounded-small border px-3 py-2 text-sm font-semibold"
                        :class="
                          selectedRoomId === room.id
                            ? ''
                            : 'bg-white text-blue-900 border-gray-200'
                        "
                        :style="selectedChoiceStyle(selectedRoomId === room.id)"
                        type="button"
                        @click="selectedRoomId = room.id"
                      >
                        {{ room.name }}
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
                  >
                    {{ $t("reservations.party-size") }}
                  </div>
                  <div class="grid grid-cols-5 gap-2">
                    <button
                      v-for="size in partySizes"
                      :key="`desktop-${size}`"
                      class="h-11 rounded-small border text-base font-bold"
                      :class="
                        partySize === size
                          ? ''
                          : 'bg-white text-blue-900 border-gray-200'
                      "
                      :style="selectedChoiceStyle(partySize === size)"
                      type="button"
                      @click="partySize = size"
                    >
                      {{ size }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="reservationStep === 'datetime'">
              <div class="grid grid-cols-[300px_minmax(0,360px)] gap-8">
                <div>
                  <div
                    class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
                  >
                    {{ $t("reservations.date") }}
                  </div>
                  <div :class="calendarPanelClass">
                    <n-date-picker
                      v-model:value="selectedDay"
                      panel
                      size="large"
                      :is-date-disabled="dateIsDisabled"
                      :first-day-of-week="0"
                      :actions="[]"
                    >
                      <template #prev-year>
                        <div />
                      </template>
                      <template #prev-month>
                        <c-icon
                          name="ArrowBackIosRound"
                          size="0.9rem"
                          class="text-blue-900"
                        />
                      </template>
                      <template #next-month>
                        <c-icon
                          name="ArrowForwardIosRound"
                          size="0.9rem"
                          class="text-blue-900"
                        />
                      </template>
                      <template #next-year>
                        <div />
                      </template>
                    </n-date-picker>
                  </div>
                </div>

                <div>
                  <div
                    class="mb-3 text-xs font-extrabold uppercase tracking-[0.06em] text-blue-900"
                  >
                    {{ $t("reservations.time") }}
                  </div>
                  <div
                    v-if="loadingAvailability"
                    class="flex justify-center py-10"
                  >
                    <c-loading-spinner />
                  </div>
                  <div v-else-if="availabilityGroups.length" class="space-y-5">
                    <div v-for="group in availabilityGroups" :key="group.label">
                      <div class="mb-2 text-sm font-semibold text-gray-400">
                        {{ group.label }}
                      </div>
                      <div class="grid grid-cols-4 gap-2">
                        <button
                          v-for="slot in group.slots"
                          :key="`desktop-${group.label}-${slot.time}`"
                          class="h-11 rounded-small border px-2 font-bold"
                          :class="slotClass(slot)"
                          :style="slotStyle(slot)"
                          :disabled="!slot.available"
                          type="button"
                          @click="selectedSlot = slot.time"
                        >
                          {{ slot.time }}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="rounded-small bg-white p-5 text-center font-semibold text-gray-400"
                  >
                    {{ $t("reservations.no-slots") }}
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              <n-input
                v-model:value="comment"
                type="textarea"
                :autosize="{ minRows: 4, maxRows: 6 }"
                :placeholder="$t('reservations.comment-placeholder')"
              />
            </div>
          </div>

          <div
            class="mt-auto flex items-center justify-between gap-6 border-t border-[#E7D7CC] pt-6"
          >
            <div class="min-w-0">
              <div class="flex items-center gap-3 font-semibold text-blue-900">
                <c-icon name="EventAvailableRound" size="1.3rem" />
                <span>{{ summaryText }}</span>
              </div>
              <div class="mt-5 text-xs text-gray-400">
                {{ $t("reservations.complete-info-to-book") }}
              </div>
            </div>
            <button
              v-if="!isFirstStep"
              class="h-[52px] min-w-[104px] rounded-small border border-gray-200 bg-white px-6 text-base font-bold text-blue-900"
              type="button"
              @click="goPreviousStep"
            >
              {{ $t("reservations.back") }}
            </button>
            <button
              class="h-[52px] min-w-[134px] rounded-small border px-8 text-base font-bold shadow-lg"
              :class="
                canContinue ? '' : 'bg-gray-200 text-gray-400 border-gray-200'
              "
              :style="canContinue ? selectedChoiceStyle(true) : null"
              :disabled="!canContinue"
              type="button"
              @click="handlePrimaryStepAction"
            >
              {{ primaryActionText }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api.js";
import { mapActions, mapState } from "pinia";
import { authStore } from "../stores/auth";
import moment from "moment";
import CIcon from "../../../core-ui/src/components/CIcon.vue";
import CLoadingSpinner from "../../../core-ui/src/components/CLoadingSpinner.vue";
import CImage from "../../../core-ui/src/components/CImage.vue";
import googleConversion from "@/googleConversion.js";
import utils from "@comeback/core/src/utils.js";

export default {
  name: "ReservationPage",
  components: {
    CIcon,
    CLoadingSpinner,
    CImage,
  },
  data() {
    return {
      rooms: [],
      schedules: [],
      closedDays: [],
      config: {
        maxPartySize: 9,
        futureBookingDays: 90,
      },
      availability: [],
      partySize: 2,
      selectedRoomId: null,
      selectedDay: new Date().getTime(),
      selectedSlot: null,
      comment: "",
      loadingAvailability: false,
      reservationStep: "details",
    };
  },
  async mounted() {
    googleConversion.parseAndStoreToken();
    await this.ensureLocationInRoute();
    await this.refreshSetup();
    await this.refreshAvailability();
  },
  computed: {
    ...mapState(authStore, [
      "organization",
      "currentLocation",
      "currentCustomer",
      "reservation",
      "branding",
      "locations",
    ]),
    activeLocation() {
      return this.currentLocation || this.locations[0] || {};
    },
    activeLocationId() {
      return this.activeLocation?.id;
    },
    reservationSteps() {
      return [
        { key: "details", label: this.$t("reservations.details") },
        { key: "datetime", label: this.$t("reservations.date-time") },
        { key: "comment", label: this.$t("reservations.comment") },
      ];
    },
    currentStepIndex() {
      let stepIndex = this.reservationSteps.findIndex(
        (step) => step.key === this.reservationStep
      );
      return Math.max(0, stepIndex);
    },
    currentStepLabel() {
      return this.reservationSteps[this.currentStepIndex]?.label;
    },
    isFirstStep() {
      return this.currentStepIndex === 0;
    },
    isFinalStep() {
      return this.currentStepIndex === this.reservationSteps.length - 1;
    },
    canContinue() {
      if (this.reservationStep === "details") {
        return !!this.activeLocation?.id && !!this.partySize;
      }
      if (this.reservationStep === "datetime") {
        return (
          !!this.selectedDay &&
          !this.dateIsDisabled(this.selectedDay) &&
          !!this.selectedSlot
        );
      }
      return !!this.selectedSlot;
    },
    primaryActionText() {
      if (this.isFinalStep) return this.$t("reservations.reserve-table");
      return this.$t("reservations.continue");
    },
    calendarPanelClass() {
      return "[&_.n-date-panel-date--excluded]:invisible [&_.n-date-panel-date--excluded]:pointer-events-none [&_.n-date-panel-month__fast-next]:invisible [&_.n-date-panel-month__fast-next]:pointer-events-none [&_.n-date-panel-month__fast-prev]:invisible [&_.n-date-panel-month__fast-prev]:pointer-events-none [&_.n-date-panel]:m-0 [&_.n-date-panel]:rounded-small [&_.n-date-panel]:border [&_.n-date-panel]:border-gray-200 [&_.n-date-panel]:bg-white [&_.n-date-panel]:shadow-none";
    },
    locationsForSelector() {
      let locations = this.locations.length
        ? this.locations
        : [this.activeLocation];
      return locations.filter((location) => location?.id);
    },
    partySizes() {
      let maxPartySize = Math.max(1, Number(this.config.maxPartySize) || 9);
      return Array.from(
        { length: Math.min(maxPartySize, 30) },
        (_, index) => index + 1
      );
    },
    roomOptions() {
      return [
        { id: null, name: this.$t("reservations.any-area") },
        ...this.rooms,
      ];
    },
    availabilityGroups() {
      return this.availability
        .map((group) => ({
          ...group,
          slots: group.slots || [],
        }))
        .filter((group) => group.slots.length > 0);
    },
    availableGroups() {
      return this.availability
        .map((group) => ({
          ...group,
          slots: (group.slots || []).filter((slot) => slot.available),
        }))
        .filter((group) => group.slots.length > 0);
    },
    selectedDateKey() {
      return moment(this.selectedDay).format("YYYY-MM-DD");
    },
    summaryText() {
      if (!this.selectedSlot) return this.$t("reservations.pick-date-time");
      return this.$t("reservations.summary", {
        partySize: this.partySize,
        date: moment(this.selectedDay).format("DD/MM"),
        time: this.selectedSlot,
      });
    },
    pageBackgroundColor() {
      return this.branding?.backgroundColor || "#F5F1EB";
    },
    heroImageId() {
      return (
        this.branding?.signupDesktopCoverImageId ||
        this.branding?.signupMobileCoverImageId ||
        this.branding?.coverImageId ||
        this.branding?.logoImageId
      );
    },
    heroTitle() {
      return (
        this.organization?.name ||
        this.activeLocation?.name ||
        this.$t("reservations.signup-subtitle")
      );
    },
    heroSubtitle() {
      return [this.activeLocation?.name, this.activeLocation?.city]
        .filter(Boolean)
        .join(" · ");
    },
    locationDetails() {
      return [
        {
          icon: "LocationOnFilled",
          text: this.activeLocation?.address,
        },
        {
          icon: "EventAvailableRound",
          text: this.scheduleSummary,
        },
      ].filter((detail) => detail.text);
    },
    scheduleSummary() {
      if (!this.schedules?.length) return null;
      return this.schedules
        .slice(0, 2)
        .map((schedule) => {
          let label = schedule.label ? `${schedule.label}: ` : "";
          return `${label}${this.formatScheduleTime(
            schedule.startTime
          )}-${this.formatScheduleTime(schedule.endTime)}`;
        })
        .join(" · ");
    },
    buttonColor() {
      let accentColor = this.branding?.accentColor || "#545383";
      let backgroundColor = this.branding?.backgroundColor || "#FFFFFF";
      if (utils.colorIsDark(accentColor)) return accentColor;
      return utils.colorIsDark(backgroundColor) ? backgroundColor : accentColor;
    },
    buttonTextColor() {
      return utils.colorIsDark(this.buttonColor) ? "#FFFFFF" : "#222134";
    },
    buttonBorderColor() {
      return utils.colorIsDark(this.buttonColor) ? this.buttonColor : "#222134";
    },
  },
  methods: {
    ...mapActions(authStore, ["updateReservation"]),
    async ensureLocationInRoute() {
      if (this.currentLocation?.id || !this.locations.length) return;
      await this.$router.replace({
        name: "reservationPage",
        params: {
          ...this.$route.params,
          slugNameOrLocationId: this.locationRouteKey(this.locations[0]),
        },
        query: { ...this.$route.query },
      });
    },
    async handleLocationSelected(location) {
      if (!location?.id || location.id === this.activeLocation?.id) return;
      this.selectedSlot = null;
      this.availability = [];
      await this.$router.push({
        name: "reservationPage",
        params: {
          ...this.$route.params,
          slugNameOrLocationId: this.locationRouteKey(location),
        },
        query: { ...this.$route.query },
      });
    },
    locationRouteKey(location) {
      return location.slug || location.id;
    },
    goToStep(stepKey) {
      let nextStepIndex = this.reservationSteps.findIndex(
        (step) => step.key === stepKey
      );
      if (nextStepIndex < 0 || nextStepIndex > this.currentStepIndex) return;
      this.reservationStep = stepKey;
    },
    goPreviousStep() {
      if (this.isFirstStep) return;
      this.reservationStep =
        this.reservationSteps[this.currentStepIndex - 1].key;
    },
    goNextStep() {
      if (!this.canContinue || this.isFinalStep) return;
      this.reservationStep =
        this.reservationSteps[this.currentStepIndex + 1].key;
    },
    handlePrimaryStepAction() {
      if (!this.canContinue) return;
      if (this.isFinalStep) {
        this.bookReservation();
        return;
      }
      this.goNextStep();
    },
    async refreshSetup() {
      if (!this.activeLocation?.id) return;
      let response = await api.get(
        `/reservations/location/${this.activeLocation.id}/setup`
      );
      this.rooms = response.data.rooms || [];
      this.schedules = response.data.schedules || [];
      this.closedDays = response.data.closedDays || [];
      this.config = {
        ...this.config,
        ...(response.data.config || {}),
      };
      if (this.partySize > this.config.maxPartySize) {
        this.partySize = this.config.maxPartySize;
      }
      if (
        this.selectedRoomId &&
        !this.rooms.some((room) => room.id === this.selectedRoomId)
      ) {
        this.selectedRoomId = null;
      }
    },
    async refreshAvailability() {
      if (!this.activeLocation?.id) return;
      this.loadingAvailability = true;
      this.selectedSlot = null;
      try {
        let response = await api.get(
          `/reservations/location/${this.activeLocation.id}/availability`,
          {
            params: {
              date: this.selectedDateKey,
              partySize: this.partySize,
              roomId: this.selectedRoomId,
            },
          }
        );
        this.availability = response.data || [];
      } finally {
        this.loadingAvailability = false;
      }
    },
    slotClass(slot) {
      if (!slot.available) {
        return "bg-[#F4EEE9] text-[#CBBDAF] border-[#E6D8CE] line-through cursor-not-allowed";
      }
      if (this.selectedSlot === slot.time) {
        return "";
      }
      return "bg-white text-blue-900 border-gray-200";
    },
    slotStyle(slot) {
      return this.selectedChoiceStyle(
        slot.available && this.selectedSlot === slot.time
      );
    },
    selectedChoiceStyle(selected) {
      if (!selected) return null;
      return {
        backgroundColor: this.buttonColor,
        borderColor: this.buttonBorderColor,
        color: this.buttonTextColor,
      };
    },
    locationCardStyle(selected) {
      if (!selected) return null;
      return {
        borderColor: this.buttonColor,
      };
    },
    formatScheduleTime(time) {
      return String(time || "").slice(0, 5);
    },
    dateIsDisabled(timestamp) {
      let today = moment().startOf("day").valueOf();
      if (timestamp < today) return true;
      let lastBookableDay = moment()
        .startOf("day")
        .add(Number(this.config.futureBookingDays) || 90, "days")
        .valueOf();
      if (timestamp > lastBookableDay) return true;
      let dateKey = moment(timestamp).format("YYYY-MM-DD");
      return this.closedDays.some((closedDay) =>
        this.closedDayMatchesDate(closedDay, dateKey)
      );
    },
    closedDayMatchesDate(closedDay, dateKey) {
      let startDate = closedDay.startDate;
      let endDate = closedDay.endDate || closedDay.startDate;
      if (!startDate || !endDate || !dateKey) return false;
      if (closedDay.yearly) {
        let dayMonth = dateKey.slice(5);
        let startDayMonth = startDate.slice(5);
        let endDayMonth = endDate.slice(5);
        if (startDayMonth <= endDayMonth) {
          return dayMonth >= startDayMonth && dayMonth <= endDayMonth;
        }
        return dayMonth >= startDayMonth || dayMonth <= endDayMonth;
      }
      return dateKey >= startDate && dateKey <= endDate;
    },
    async bookReservation() {
      if (!this.selectedSlot || !this.activeLocation?.id) return;
      let reservation = {
        partySize: this.partySize,
        roomId: this.selectedRoomId,
        startTime: moment(this.selectedDay)
          .set({
            hour: Number(this.selectedSlot.split(":")[0]),
            minute: Number(this.selectedSlot.split(":")[1]),
            second: 0,
            millisecond: 0,
          })
          .toDate(),
        comment: this.comment,
      };
      this.updateReservation(reservation);
      if (this.currentCustomer?.id) {
        try {
          await api.post(
            `/reservations/location/${this.activeLocation.id}/reservation`,
            { ...reservation, customerId: this.currentCustomer.id }
          );
          googleConversion.sendConversionEvent();
          this.$router.push({
            name: "downloadPage",
            params: { ...this.$route.params },
          });
        } catch {
          this.$notificationGenerator.error(
            this.$t("reservations.no-availability")
          );
          await this.refreshAvailability();
        }
      } else {
        this.$router.push({
          name: "createCustomerPage",
          params: { ...this.$route.params },
        });
      }
    },
  },
  watch: {
    partySize() {
      this.refreshAvailability();
    },
    selectedRoomId() {
      this.refreshAvailability();
    },
    selectedDay() {
      this.refreshAvailability();
    },
    activeLocationId(newLocationId, oldLocationId) {
      if (!newLocationId || !oldLocationId || newLocationId === oldLocationId) {
        return;
      }
      this.refreshSetup().then(() => this.refreshAvailability());
    },
  },
};
</script>
